import { Injectable, inject } from '@angular/core';

import { CreateScheduledEventStore } from 'src/app/features/events/state/create-scheduled-event.state';
import { SelectedScheduleEventDetailsStore } from 'src/app/features/events/state/selected-schedule-event.state';
import { ConfirmationModalPayload } from 'src/app/shared/components/modals/confirmation-modal/axle-confirmation-modal.component';
import { CANNOT_REMOVE_OWN_EVENT_ACCESS } from 'src/app/shared/constants/error.constants';
import { buildEventDayErrorResponse } from 'src/app/shared/functions/error.functions';
import { openUnsavedChangesModal } from 'src/app/shared/functions/modal.functions';
import { ErrorResponse } from 'src/app/shared/models/rxMethod.models';
import { UPDATE_EVENT_OWN_ACCESS_ERROR_MESSAGE } from 'src/app/shared/services/events/access/event-access.constants';
import {
  EventDetailsDto,
  UpdateEventPayload
} from 'src/app/shared/services/events/events.models';
import { openHostChangeConfirmationModal } from 'src/app/shared/services/events/host-management/event-host-management.functions';
import { EventSchedulerStore } from 'src/app/shared/services/events/scheduler/event-scheduler.state';
import { ModalService } from 'src/app/shared/services/modal/modal.service';
import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { UserStore } from 'src/app/shared/services/user/user.state';

interface SavePendingUpdatesOptions {
  onSuccess: () => void;
  onFail?: () => void;
  showSuccessToast?: boolean;
}

interface CapacityViolation {
  maxIndividualsPerGuestOrg?: number | null;
  numGuestOrgs?: number | null;
  numRegisteredGuests?: number | null;
}

type CapacityUpdatesPatcher = (capacityUpdates: UpdateEventPayload) => void;

const invalidScheduledEventFormMessage =
  'You are missing a required field. Please make sure everything is valid before updating.';

function hasCapacityViolationValue(
  value: number | null | undefined
): value is number {
  return value !== null && value !== undefined;
}

@Injectable({ providedIn: 'root' })
export class UpdateEventScheduleModeSaveService {
  private _createScheduledEventStore = inject(CreateScheduledEventStore);
  private _eventSchedulerStore = inject(EventSchedulerStore);
  private _modalService = inject(ModalService);
  private _selectedScheduleEventStore = inject(
    SelectedScheduleEventDetailsStore
  );
  private _toastService = inject(AxleToastService);
  private _userStore = inject(UserStore);

  private _canSavePendingUpdatesChecker: (() => boolean) | null = null;
  private _capacityUpdatesPatcher: CapacityUpdatesPatcher | null = null;
  private _senderLockResetHandlers = new Set<() => void>();

  registerCanSavePendingUpdatesChecker(checker: (() => boolean) | null) {
    this._canSavePendingUpdatesChecker = checker;
  }

  /**
   * Registers a handler that re-syncs an open calendar-invite sender control
   * after a locked sender is reset from event details.
   */
  registerSenderLockResetHandler(handler: () => void) {
    this._senderLockResetHandlers.add(handler);
  }

  unregisterSenderLockResetHandler(handler: () => void) {
    this._senderLockResetHandlers.delete(handler);
  }

  /**
   * Re-syncs every open calendar-invite sender control after locked senders
   * have been reset from event details following a lock conflict.
   */
  notifySenderLockReset() {
    this._senderLockResetHandlers.forEach((handler) => handler());
  }

  registerCapacityUpdatesPatcher(patcher: CapacityUpdatesPatcher | null) {
    this._capacityUpdatesPatcher = patcher;
  }

  shouldBlockNavigation(): boolean {
    return (
      !!this._createScheduledEventStore.eventId() &&
      this._createScheduledEventStore.hasUnsavedChanges()
    );
  }

  discardPendingChanges() {
    this._createScheduledEventStore.clearUpdates();
  }

  confirmNavigationAway(): Promise<boolean> {
    if (!this.shouldBlockNavigation()) return Promise.resolve(true);

    return new Promise((resolve) => {
      openUnsavedChangesModal(this._modalService, {
        onSave: () => {
          this.savePendingUpdates({
            onSuccess: () => resolve(true),
            onFail: () => resolve(false)
          });
        },
        onDiscard: () => {
          this.discardPendingChanges();
          resolve(true);
        }
      });
    });
  }

  saveEventAccessSection(options: SavePendingUpdatesOptions) {
    const releaseContext = this._readEventAccessReleaseContext();
    const updatePayload = releaseContext.eventAccessUpdatePayload;
    const hasInternalAccessUpdate = Object.keys(updatePayload).length > 0;

    if (
      !hasInternalAccessUpdate &&
      !releaseContext.hasReleaseToHubsUpdate &&
      !releaseContext.hasCorpAxeRelease &&
      !releaseContext.hasCrmProviderRelease
    ) {
      this._completeNoOpSave(options);
      return;
    }

    const eventId = releaseContext.eventId;
    if (!eventId) {
      this._failMissingEventId(options);
      return;
    }

    this._runReleaseAndUpdateChain({
      eventId,
      updatePayload,
      releaseToHub: releaseContext.releaseToHub,
      hasReleaseToHubsUpdate: releaseContext.hasReleaseToHubsUpdate,
      hasCorpAxeRelease: releaseContext.hasCorpAxeRelease,
      hasCrmProviderRelease: releaseContext.hasCrmProviderRelease,
      options
    });
  }

  saveUpdatePayload(
    payload: UpdateEventPayload,
    options: SavePendingUpdatesOptions
  ) {
    if (!Object.keys(payload).length) {
      this._completeNoOpSave(options);
      return;
    }

    const eventId = this._createScheduledEventStore.eventId();
    if (!eventId) {
      this._failMissingEventId(options);
      return;
    }

    this._updateEvent(eventId, payload, options);
  }

  savePendingUpdates(options: SavePendingUpdatesOptions) {
    if (!this._canSavePendingUpdates()) {
      this._toastService.addError(invalidScheduledEventFormMessage, 10000);
      options.onFail?.();
      return;
    }

    const releaseContext = this._readEventAccessReleaseContext();
    const mergedUpdatePayload = this._mergedUpdatePayload();
    const hasInternalAccessUpdate =
      Object.keys(releaseContext.eventAccessUpdatePayload).length > 0;
    const hasUpdateEventPayload = Object.keys(mergedUpdatePayload).length > 0;

    if (
      !hasInternalAccessUpdate &&
      !releaseContext.hasReleaseToHubsUpdate &&
      !releaseContext.hasCorpAxeRelease &&
      !releaseContext.hasCrmProviderRelease &&
      !hasUpdateEventPayload
    ) {
      this._completeNoOpSave(options);
      return;
    }

    const eventId = releaseContext.eventId;
    if (!eventId) {
      this._failMissingEventId(options);
      return;
    }

    this._runReleaseAndUpdateChain({
      eventId,
      updatePayload: mergedUpdatePayload,
      releaseToHub: releaseContext.releaseToHub,
      hasReleaseToHubsUpdate: releaseContext.hasReleaseToHubsUpdate,
      hasCorpAxeRelease: releaseContext.hasCorpAxeRelease,
      hasCrmProviderRelease: releaseContext.hasCrmProviderRelease,
      options
    });
  }

  private _canSavePendingUpdates(): boolean {
    if (!this._canSavePendingUpdatesChecker) return true;

    return this._canSavePendingUpdatesChecker();
  }

  private _readEventAccessReleaseContext() {
    const releaseToHub =
      this._createScheduledEventStore.eventAccessReleaseToHubsValue();

    return {
      eventId: this._createScheduledEventStore.eventId(),
      releaseToHub,
      hasReleaseToHubsUpdate: releaseToHub !== null,
      hasCorpAxeRelease:
        this._createScheduledEventStore.eventAccessShouldReleaseToCorpAxe(),
      hasCrmProviderRelease:
        this._createScheduledEventStore.eventAccessShouldReleaseToCrmProvider(),
      eventAccessUpdatePayload:
        this._createScheduledEventStore.eventAccessSectionUpdatePayload()
    };
  }

  private _failMissingEventId(options: SavePendingUpdatesOptions) {
    this._toastService.addError(
      'Failed to update event. Please try again.',
      10000
    );
    options.onFail?.();
  }

  private _completeNoOpSave(options: SavePendingUpdatesOptions) {
    this._createScheduledEventStore.pruneNoOpUpdates();

    if (options.showSuccessToast) {
      this._toastService.addSuccess('Event Updated');
    }

    options.onSuccess();
  }

  private _completeScheduledEventSave(
    options: SavePendingUpdatesOptions,
    dto?: EventDetailsDto
  ) {
    if (dto) {
      this._createScheduledEventStore.hydrateFromEventDetailsDto(dto);
    } else {
      this._createScheduledEventStore.clearUpdates();
    }

    if (options.showSuccessToast) {
      this._toastService.addSuccess('Event Updated');
    }

    options.onSuccess();
  }

  private _runReleaseAndUpdateChain(params: {
    eventId: string;
    updatePayload: UpdateEventPayload;
    releaseToHub: boolean | null;
    hasReleaseToHubsUpdate: boolean;
    hasCorpAxeRelease: boolean;
    hasCrmProviderRelease: boolean;
    options: SavePendingUpdatesOptions;
  }) {
    const {
      eventId,
      updatePayload,
      releaseToHub,
      hasReleaseToHubsUpdate,
      hasCorpAxeRelease,
      hasCrmProviderRelease,
      options
    } = params;
    const hasUpdateEventStep = Object.keys(updatePayload).length > 0;

    const continueAfterHubRelease = () => {
      if (hasUpdateEventStep) {
        this._updateEvent(eventId, updatePayload, options);
        return;
      }

      this._completeScheduledEventSave(options);
    };

    const continueAfterCorpAxeRelease = () => {
      if (hasReleaseToHubsUpdate && releaseToHub !== null) {
        this._selectedScheduleEventStore.releaseEventToHub({
          eventId,
          releaseToHub,
          onSuccess: () => {
            this._createScheduledEventStore.updateState({
              releasedToHubs: releaseToHub
            });
            continueAfterHubRelease();
          },
          onFail: () => {
            this._toastService.addError(
              releaseToHub
                ? 'Failed to release to hub'
                : 'Failed to hide event from hub',
              10000
            );
            options.onFail?.();
          }
        });
        return;
      }

      continueAfterHubRelease();
    };

    const continueAfterCrmProviderRelease = () => {
      if (hasCorpAxeRelease) {
        this._releaseEventToCorpAxe(eventId, continueAfterCorpAxeRelease, () =>
          options.onFail?.()
        );
        return;
      }

      continueAfterCorpAxeRelease();
    };

    const runReleaseChain = () => {
      if (hasCrmProviderRelease) {
        this._releaseEventToCrmProvider(
          eventId,
          continueAfterCrmProviderRelease,
          () => options.onFail?.()
        );
        return;
      }

      continueAfterCrmProviderRelease();
    };

    const confirmCorpAxeThenRun = () => {
      if (!hasCorpAxeRelease) {
        runReleaseChain();
        return;
      }

      this._openCorpAxeReleaseConfirmation(runReleaseChain, () =>
        options.onFail?.()
      );
    };

    if (hasCrmProviderRelease) {
      this._openCrmProviderReleaseConfirmation(confirmCorpAxeThenRun, () =>
        options.onFail?.()
      );
      return;
    }

    confirmCorpAxeThenRun();
  }

  private _mergedUpdatePayload(): UpdateEventPayload {
    return {
      ...this._createScheduledEventStore.generalSectionUpdatePayload(),
      ...this._createScheduledEventStore.datesAndLocationSectionUpdatePayload(),
      ...this._createScheduledEventStore.coordinatorsSectionUpdatePayload(),
      ...this._createScheduledEventStore.guestRegistrationSectionUpdatePayload(),
      ...this._createScheduledEventStore.guestCalendarInvitesSectionUpdatePayload(),
      ...this._createScheduledEventStore.hostCalendarInvitesSectionUpdatePayload(),
      ...this._createScheduledEventStore.colleagueCalendarInvitesSectionUpdatePayload(),
      ...this._createScheduledEventStore.hostScheduleSectionUpdatePayload(),
      ...this._createScheduledEventStore.landingSettingsSectionUpdatePayload(),
      ...this._createScheduledEventStore.hostPortalSectionUpdatePayload(),
      ...this._createScheduledEventStore.guestPortalSectionUpdatePayload(),
      ...this._createScheduledEventStore.additionalContentPortalSectionUpdatePayload(),
      ...this._createScheduledEventStore.additionalFeaturesSectionUpdatePayload(),
      ...this._createScheduledEventStore.eventAccessSectionUpdatePayload()
    };
  }

  private _openCrmProviderReleaseConfirmation(
    onConfirm: () => void,
    onFail?: () => void
  ) {
    const providerName = this._userStore.crmExternalProviderName();

    this._openDestructiveReleaseConfirmation(
      {
        header: `Release to ${providerName}`,
        subheader: 'This action cannot be undone. Are you sure?',
        buttonText: `Release to ${providerName}`
      },
      onConfirm,
      onFail
    );
  }

  private _openCorpAxeReleaseConfirmation(
    onConfirm: () => void,
    onFail?: () => void
  ) {
    this._openDestructiveReleaseConfirmation(
      {
        header: 'Release to CorpAxe',
        subheader:
          "This will publish the event to CorpAxe's forward-looking calendar, where it will be visible externally.\n\nThis action cannot be undone. Are you sure?",
        buttonText: 'Release to CorpAxe'
      },
      onConfirm,
      onFail
    );
  }

  private _openDestructiveReleaseConfirmation(
    copy: Pick<ConfirmationModalPayload, 'header' | 'subheader' | 'buttonText'>,
    onConfirm: () => void,
    onFail?: () => void
  ) {
    let confirmed = false;

    this._modalService.openModal<ConfirmationModalPayload, undefined>(
      'confirmation',
      {
        ...copy,
        type: 'destructive',
        secondaryButtonText: 'Nevermind',
        confirmClicked: () => {
          confirmed = true;
          this._modalService.close('confirmation');
        },
        secondaryButtonClicked: () => {
          this._modalService.close('confirmation');
        }
      },
      () => {
        if (confirmed) {
          onConfirm();
        } else {
          onFail?.();
        }
      }
    );
  }

  private _releaseEventToCrmProvider(
    eventId: string,
    onComplete: () => void,
    onFail?: () => void
  ) {
    this._selectedScheduleEventStore.releaseEventToCrmProvider({
      eventId,
      onSuccess: () => {
        this._createScheduledEventStore.updateState({
          releasedToCrmProvider: true
        });
        onComplete();
      },
      onFail: () => {
        this._toastService.addError(
          `Failed to release to ${this._userStore.crmExternalProviderName()}`,
          10000
        );
        onFail?.();
      }
    });
  }

  private _releaseEventToCorpAxe(
    eventId: string,
    onComplete: () => void,
    onFail?: () => void
  ) {
    this._selectedScheduleEventStore.releaseEventToCorpAxe({
      eventId,
      onSuccess: (details) => {
        this._createScheduledEventStore.updateState({
          releasedToCorpAxe: true,
          releasedToCorpAxeTimestamp: details.releasedToCorpAxeTimestamp
        });
        onComplete();
      },
      onFail: (error) => {
        this._toastService.addError(
          this._corpAxeReleaseErrorMessage(error),
          10000
        );
        onFail?.();
      }
    });
  }

  private _corpAxeReleaseErrorMessage(error?: ErrorResponse): string {
    if (error?.errorCode !== 942) return 'Failed to release to CorpAxe';

    const errorMessage = error.extraInformation?.errorMessage;
    if (typeof errorMessage === 'string' && errorMessage.length > 0) {
      return errorMessage;
    }

    return 'Failed to release to CorpAxe';
  }

  private _updateEvent(
    eventId: string,
    payload: UpdateEventPayload,
    options: SavePendingUpdatesOptions,
    override = false
  ) {
    this._selectedScheduleEventStore.updateEvent({
      eventId,
      payload,
      override,
      onSuccess: (dto) => {
        this._refreshSchedulerHosts(eventId, payload);
        this._completeScheduledEventSave(options, dto);
      },
      onFail: (error) => {
        if (!this._handleUpdateEventError(error, eventId, payload, options)) {
          options.onFail?.();
        }
      }
    });
  }

  /**
   * Host changes can cancel meetings and change the slideout's host list, and
   * the scheduler only refetches those when the event changes.
   */
  private _refreshSchedulerHosts(eventId: string, payload: UpdateEventPayload) {
    if (!payload.hostOrgIds && !payload.hostUserIds) return;

    this._eventSchedulerStore.fetchMissionControl({ eventId });
    this._eventSchedulerStore.fetchLightHosts({ eventId });
  }

  private _handleUpdateEventError(
    error: ErrorResponse | undefined,
    eventId: string,
    payload: UpdateEventPayload,
    options: SavePendingUpdatesOptions
  ): boolean {
    switch (error?.errorCode) {
      case 301:
        openHostChangeConfirmationModal(this._modalService, {
          type: 'REMOVE_FROM_EVENT',
          entityType: 'BULK',
          scheduleMode: true,
          onConfirm: () => this._updateEvent(eventId, payload, options, true),
          onCancel: () => options.onFail?.()
        });
        return true;
      case 174:
        this._toastService.addError(
          'An event needs at least one day. Please add one.',
          10000
        );
        break;
      case 521:
        this._toastService.addError(
          'Please clear meetings, activities and presentations before making a day unofficial',
          10000
        );
        break;
      case 175:
        this._toastService.addError('Invalid timezone', 10000);
        break;
      case 189:
        this._refreshEventDetailsAfterConflict();
        this._toastService.addError(
          'Calendar invite sender cannot be changed after invites have been sent out for this audience.',
          10000
        );
        break;
      case 106:
        this._toastService.addError(
          'A required field is missing or invalid.',
          10000
        );
        break;
      case 215:
        this._toastService.addError(
          'You are missing a required field. Please make sure everything is valid before updating.',
          10000
        );
        break;
      case 219:
        this._toastService.addError(
          'Invalid locations. Please make sure they are not empty before updating.',
          10000
        );
        break;
      case 220:
        this._toastService.addError(
          'Event dates cannot be in the past.',
          10000
        );
        break;
      case 411:
        this._openInvalidCapacityModal(
          payload,
          options,
          error.extraInformation
        );
        return true;
      case 918:
        this._toastService.addError(
          `Please remove the following before removing day(s): ${buildEventDayErrorResponse(error)}`,
          10000
        );
        break;
      case CANNOT_REMOVE_OWN_EVENT_ACCESS:
        this._toastService.addError(
          UPDATE_EVENT_OWN_ACCESS_ERROR_MESSAGE,
          10000
        );
        break;
      case 942:
        this._toastService.addError(error.extraInformation.errorMessage, 10000);
        break;
      default:
        this._toastService.addError(
          'Failed to update event. Please try again.',
          10000
        );
    }

    return false;
  }

  private _refreshEventDetailsAfterConflict() {
    const eventId = this._createScheduledEventStore.eventId();
    if (!eventId) return;

    this._selectedScheduleEventStore.fetchDetails({
      eventId,
      force: true,
      onSuccess: (dto) => {
        this._createScheduledEventStore.resetCalendarInviteSenderLockFieldsFromDto(
          dto
        );
        this.notifySenderLockReset();
      }
    });
  }

  private _openInvalidCapacityModal(
    payload: UpdateEventPayload,
    options: SavePendingUpdatesOptions,
    violation: CapacityViolation | undefined
  ) {
    const capacityUpdates = this._capacityUpdatesFromViolation(violation);

    this._modalService.openModal<ConfirmationModalPayload, undefined>(
      'confirmation',
      {
        header: 'Invalid Capacity',
        subheader: this._buildCapacitySubheader(violation),
        buttonText: 'Update',
        secondaryButtonText: 'Cancel',
        confirmClicked: () => {
          this._modalService.close('confirmation');
          this._retryWithCapacityUpdates(payload, capacityUpdates, options);
        },
        secondaryButtonClicked: () => {
          this._modalService.close('confirmation');
          options.onFail?.();
        }
      }
    );
  }

  private _buildCapacitySubheader(
    violation: CapacityViolation | undefined
  ): string {
    const fallback =
      'You have specified a capacity that is lower than the current event registrations.';

    if (!violation) return fallback;

    const prefix =
      'You have specified a capacity that is lower than the number of';
    const update = 'Update event capacity to';

    if (hasCapacityViolationValue(violation.numRegisteredGuests)) {
      return `${prefix} registered guests: ${violation.numRegisteredGuests}.\n\n${update} ${violation.numRegisteredGuests} guests?`;
    }

    if (
      hasCapacityViolationValue(violation.numGuestOrgs) &&
      hasCapacityViolationValue(violation.maxIndividualsPerGuestOrg)
    ) {
      return `${prefix} registered guest organizations: ${violation.numGuestOrgs} and the maximum number of individuals per guest organization: ${violation.maxIndividualsPerGuestOrg}.\n\n${update} ${violation.numGuestOrgs} guest organizations and ${violation.maxIndividualsPerGuestOrg} individuals per guest organization?`;
    }

    if (hasCapacityViolationValue(violation.numGuestOrgs)) {
      return `${prefix} registered guest organizations: ${violation.numGuestOrgs}.\n\n${update} ${violation.numGuestOrgs} guest organizations?`;
    }

    if (hasCapacityViolationValue(violation.maxIndividualsPerGuestOrg)) {
      return `${prefix} registered guests per guest organization: ${violation.maxIndividualsPerGuestOrg}.\n\n${update} ${violation.maxIndividualsPerGuestOrg} individuals per guest organization?`;
    }

    return fallback;
  }

  private _capacityUpdatesFromViolation(
    violation: CapacityViolation | undefined
  ): UpdateEventPayload {
    return {
      ...(hasCapacityViolationValue(violation?.numRegisteredGuests)
        ? { individualGuestCapacity: violation.numRegisteredGuests }
        : {}),
      ...(hasCapacityViolationValue(violation?.numGuestOrgs)
        ? { guestOrgCapacity: violation.numGuestOrgs }
        : {}),
      ...(hasCapacityViolationValue(violation?.maxIndividualsPerGuestOrg)
        ? {
            individualsPerGuestOrgCapacity: violation.maxIndividualsPerGuestOrg
          }
        : {})
    };
  }

  private _updateCapacityState(capacityUpdates: UpdateEventPayload) {
    this._createScheduledEventStore.updateState({
      individualGuestCapacity:
        capacityUpdates.individualGuestCapacity ??
        this._createScheduledEventStore.individualGuestCapacity(),
      guestOrgCapacity:
        capacityUpdates.guestOrgCapacity ??
        this._createScheduledEventStore.guestOrgCapacity(),
      individualsPerGuestOrgCapacity:
        capacityUpdates.individualsPerGuestOrgCapacity ??
        this._createScheduledEventStore.individualsPerGuestOrgCapacity()
    });
  }

  private _retryWithCapacityUpdates(
    payload: UpdateEventPayload,
    capacityUpdates: UpdateEventPayload,
    options: SavePendingUpdatesOptions
  ) {
    this._capacityUpdatesPatcher?.(capacityUpdates);
    this._updateCapacityState(capacityUpdates);
    this.saveUpdatePayload(
      {
        ...payload,
        ...capacityUpdates
      },
      options
    );
  }
}
